import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import { DataGrid } from "@mui/x-data-grid";
import {
  customerCardPanelHeaderSx,
  customerCardPanelLabelSx,
  customerCardPanelTitleTypographyProps,
  customerCardPanelValueSx,
} from "./customerCardPanelStyles";

const formatVf = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "—";
  }

  return `${Number(value).toLocaleString("nl-NL", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 2,
  })}%`;
};

const getProductLabel = (productType) => {
  if (Number(productType) === 1) return "VAT";
  if (Number(productType) === 2) return "Excise";
  return "—";
};

const getPeriodLabel = (periodType) => {
  if (Number(periodType) === 1) return "p/M";
  if (Number(periodType) === 2) return "p/Q";
  return "—";
};

const formatConfiguredLabel = (item) => `${item.countryCode} ${item.productLabel}`;

const CustomerPreFinanceSummaryPanel = ({ customerId }) => {
  const [agreements, setAgreements] = useState([]);
  const [countries, setCountries] = useState([]);
  const [entitySettings, setEntitySettings] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const fetchData = useCallback(async () => {
    if (!customerId) {
      setAgreements([]);
      setEntitySettings(null);
      return;
    }

    try {
      const [agreementsResponse, countriesResponse, settingsResponse] = await Promise.all([
        axios.get(`/api/v1/customerpricingagreements/entity/${customerId}`),
        axios.get("/api/v1/allCountries"),
        axios.get(`/api/v1/entitySettings/${customerId}`).catch(() => ({ data: null })),
      ]);

      setAgreements(agreementsResponse.data || []);
      setCountries(countriesResponse.data || []);
      setEntitySettings(settingsResponse.data);
    } catch (error) {
      console.error("Error loading pre-finance summary:", error);
      setAgreements([]);
    }
  }, [customerId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const countryCodeMap = useMemo(() => {
    const map = new Map();
    countries.forEach((country) => {
      map.set(Number(country.countryId), country.countryCode || String(country.countryId));
    });
    return map;
  }, [countries]);

  const activeAgreements = useMemo(() => {
    return (agreements || [])
      .filter((agreement) => Number(agreement.vf) > 0)
      .map((agreement) => ({
        id: agreement.id,
        countryId: agreement.country,
        countryCode: countryCodeMap.get(Number(agreement.country)) || String(agreement.country),
        productType: Number(agreement.productType),
        productLabel: getProductLabel(agreement.productType),
        periodType: Number(agreement.preFinancePeriodType),
        periodLabel: getPeriodLabel(agreement.preFinancePeriodType),
        vf: agreement.vf,
      }))
      .sort((left, right) => {
        const countryCompare = left.countryCode.localeCompare(right.countryCode);
        if (countryCompare !== 0) return countryCompare;
        return left.productType - right.productType;
      });
  }, [agreements, countryCodeMap]);

  const configuredLabel = useMemo(() => {
    if (activeAgreements.length === 0) {
      return "No active agreements";
    }

    return activeAgreements.map((item) => formatConfiguredLabel(item)).join(", ");
  }, [activeAgreements]);

  const statusLabel = useMemo(() => {
    if (!entitySettings) {
      return null;
    }

    if (entitySettings.blockedPreFinance) {
      return { label: "Blocked", color: "error" };
    }

    if (!entitySettings.allowPreFinance) {
      return { label: "Not allowed", color: "warning" };
    }

    return { label: "Allowed", color: "success" };
  }, [entitySettings]);

  const handleOpenDialog = () => {
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    fetchData();
  };

  const dialogColumns = useMemo(
    () => [
      {
        field: "countryCode",
        headerName: "Country",
        width: 100,
      },
      {
        field: "productLabel",
        headerName: "Product",
        width: 100,
      },
      {
        field: "periodLabel",
        headerName: "Period",
        width: 90,
      },
      {
        field: "vf",
        headerName: "VF",
        flex: 1,
        minWidth: 90,
        valueFormatter: ({ value }) => formatVf(value),
      },
    ],
    []
  );

  return (
    <>
      <Card>
        <CardHeader
          title="Pre-finance"
          titleTypographyProps={customerCardPanelTitleTypographyProps}
          sx={customerCardPanelHeaderSx}
          action={
            <Tooltip title="View all pre-finance agreements">
              <IconButton
                aria-label="view all pre-finance agreements"
                onClick={handleOpenDialog}
                size="small"
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          }
        />
        <Divider />
        <CardContent sx={{ px: 2, pt: 1, pb: 1.25, "&:last-child": { pb: 1.25 } }}>
          <Stack spacing={0.75}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "220px minmax(0, 1fr)",
                columnGap: 1,
                alignItems: "start",
              }}
            >
              <Typography variant="body2" sx={customerCardPanelLabelSx}>
                Status:
              </Typography>
              {statusLabel ? (
                <Chip label={statusLabel.label} color={statusLabel.color} size="small" sx={{ width: "fit-content" }} />
              ) : (
                <Typography variant="body2" sx={customerCardPanelValueSx}>
                  -
                </Typography>
              )}
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "220px minmax(0, 1fr)",
                columnGap: 1,
                alignItems: "center",
                minWidth: 0,
              }}
            >
              <Typography variant="body2" sx={customerCardPanelLabelSx}>
                Configured:
              </Typography>
              <Typography
                variant="body2"
                noWrap
                title={configuredLabel}
                sx={{
                  ...customerCardPanelValueSx,
                  fontFamily: activeAgreements.length > 0 ? "monospace" : "inherit",
                  minWidth: 0,
                }}
              >
                {configuredLabel}
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Dialog open={isDialogOpen} onClose={handleCloseDialog} fullWidth maxWidth="sm">
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          Pre-finance agreements
          <IconButton aria-label="close pre-finance dialog" onClick={handleCloseDialog}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {statusLabel && (
            <Box mb={1.5}>
              <Chip label={`Status: ${statusLabel.label}`} color={statusLabel.color} size="small" />
            </Box>
          )}
          {activeAgreements.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No pricing agreements with an active VF percentage for this customer.
            </Typography>
          ) : (
            <DataGrid
              autoHeight
              density="compact"
              disableSelectionOnClick
              hideFooter={activeAgreements.length <= 10}
              rows={activeAgreements}
              columns={dialogColumns}
              pageSize={10}
              rowsPerPageOptions={[10]}
              getRowId={(row) => row.id}
              sx={{
                borderRadius: 1,
                boxShadow: 1,
                "& .MuiDataGrid-cell:hover": {
                  color: "primary.main",
                },
              }}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button variant="contained" onClick={handleCloseDialog}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CustomerPreFinanceSummaryPanel;
